import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import TableBody from "@mui/material/TableBody";
import { useMemo, useState, useEffect, useRef } from "react";
import Dropdown from "../Dropdown/Dropdown";
import Tooltip from "@mui/material/Tooltip";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import {
  StyledCardTableContainer,
  StyledTable,
  StyledTableCell,
  StyledTableContainer,
  StyledTableHead,
  StyledTableRow,
  TableScrollWrapper,
} from "./table.styles";
import TablePagination from "@mui/material/TablePagination";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useDrag, useDrop } from "react-dnd";
import InputBase from "@mui/material/InputBase";

//helpers for reordering columns
const HEADER_TYPE = "table-header-col";
const arrayMove = (arr, from, to) => {
  if (from === to) return arr;
  const copy = arr.slice();
  const [it] = copy.splice(from, 1);
  copy.splice(to, 0, it);
  return copy;
};

function formatNumber(num) {
  return num?.toLocaleString() || "-";
}

function getAllColumns(
  data,
  exclude = [
    "children",
    "id",
    "ID",
    "Id",
    "isExpanded",
    "level",
    "isVisible",
    "_id",
    "row_id",
    "rowId",
  ]
) {
  const cols = new Set();
  function traverse(rows) {
    rows.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (!exclude.includes(key)) {
          if (
            typeof row[key] === "object" &&
            row[key] !== null &&
            !Array.isArray(row[key])
          ) {
            Object.keys(row[key]).forEach((subKey) => {
              cols.add(`${key}.${subKey}`);
            });
          } else {
            cols.add(key);
          }
        }
      });
    });
  }
  traverse(data);
  return Array.from(cols);
}

function getValue(row, col) {
  return row[col];
}

//Draggable header cell — drag anywhere on the header cell
function SortableHeaderCell({
  col,
  index,
  moveHeader,
  onSort,
  isSorted,
  sortDirection,
  onSearch,
  searchValue,
  isComputedColumn = false,
  computedColumnFormula = null,
  onDeleteColumn,
  onEditComputedColumn
}) {
  const ref = useRef(null);
  const [showSearch, setShowSearch] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [, drop] = useDrop({
    accept: HEADER_TYPE,
    hover(item) {
      if (!ref.current) return;
      if (item.index === index) return;
      moveHeader(item.index, index);
      item.index = index;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: HEADER_TYPE,
    item: { index },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  // make the entire header cell draggable
  drop(drag(ref));

  const hasSearch = searchValue && searchValue.length > 0;

  return (
    <StyledTableCell
      ref={ref}
      header
      align={col.includes(".") ? "right" : "left"}
      style={{
        cursor: "grab",
        userSelect: "none",
        opacity: isDragging ? 0.6 : 1,
      }}
      sorted={isSorted ? sortDirection : undefined}
      title={col}
    >
      <Box sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        width: '100%',
        minWidth: 150
      }}>
        {/* Header row with column name and icons */}
        <Box sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: '100%'
        }}>
          <Box sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flex: 1,
            cursor: 'pointer'
          }} onClick={() => onSort(col)}>
            <span>{col}</span>
            {isComputedColumn && (
              <Tooltip
                title={
                  <Box sx={{ p: 0.5 }}>
                    <Box sx={{ fontWeight: 600, mb: 0.5, fontSize: 12 }}>
                      Calculated Column
                    </Box>
                    <Box sx={{ fontSize: 11, fontFamily: "monospace", wordBreak: "break-all" }}>
                      {computedColumnFormula || "No formula available"}
                    </Box>
                  </Box>
                }
                arrow
                placement="top"
              >
                <Box
                  component="span"
                  sx={{
                    fontSize: 9,
                    fontWeight: 600,
                    color: "#3F49A7",
                    backgroundColor: "#e0e7ff",
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    ml: 0.5,
                    cursor: "help",
                  }}
                >
                  CALC
                </Box>
              </Tooltip>
            )}
            {isSorted ? (
              sortDirection === "asc" ? (
                <ArrowDropUpIcon fontSize="small" sx={{
                  color: "#666666",
                  transition: 'transform 0.2s ease',
                  transform: 'translateY(-1px)'
                }} />
              ) : (
                <ArrowDropDownIcon fontSize="small" sx={{
                  color: "#666666",
                  transition: 'transform 0.2s ease',
                  transform: 'translateY(1px)'
                }} />
              )
            ) : (
              <ArrowDropDownIcon
                fontSize="small"
                sx={{
                  color: "#666666",
                  opacity: 0.5,
                  transition: 'all 0.2s ease',
                  transform: 'translateY(1px)'
                }}
              />
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setShowSearch(!showSearch);
              }}
              sx={{ 
                p: 0.5,
                color: hasSearch ? '#3F49A7' : '#666',
                '&:hover': { color: '#3F49A7' }
              }}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
             {isComputedColumn && (
               <>
                 {onEditComputedColumn && (
                   <Tooltip title="Edit calculated column">
                     <IconButton
                       size="small"
                       onClick={(e) => {
                         e.stopPropagation();
                         onEditComputedColumn(col);
                       }}
                       sx={{ 
                         p: 0.5,
                         color: '#666666',
                         '&:hover': { 
                           color: '#3F49A7',
                         }
                       }}
                     >
                       <EditIcon fontSize="extra-small" />
                     </IconButton>
                   </Tooltip>
                 )}
                 {onDeleteColumn && (
                   <>
                     <Tooltip title="Remove calculated column">
                       <IconButton
                         size="small"
                         onClick={(e) => {
                           e.stopPropagation();
                           setShowDeleteDialog(true);
                         }}
                         sx={{ 
                           p: 0.5,
                           color: '#666666',
                           '&:hover': { 
                             color: '#3F49A7',
                           }
                         }}
                       >
                         <CloseIcon fontSize="extra-small" />
                       </IconButton>
                     </Tooltip>
                     <ConfirmationModal
                       open={showDeleteDialog}
                       title="Remove Calculated Column"
                       message={`Are you sure you want to delete the calculated column <br/><strong>"${col}"</strong>?`}
                       onClose={() => setShowDeleteDialog(false)}
                       onConfirm={() => {
                         onDeleteColumn(col);
                         setShowDeleteDialog(false);
                       }}
                       buttonText="Remove"
                       colorCode="#3F49A7"
                     />
                   </>
                 )}
               </>
             )}
          </Box>
        </Box>

        {/* Search input */}
        {showSearch && (
          <InputBase
            size="small"
            placeholder="Search..."
            value={searchValue || ''}
            onChange={(e) => onSearch(col, e.target.value)}
            onClick={(e) => e.stopPropagation()}
            sx={{
              fontSize: 13,
              width: '100%',
              bgcolor: '#f5f5f5',
              borderRadius: 1,
              px: 1,
              py: 0.5,
              '& input': {
                p: 0,
              }
            }}
          />
        )}
      </Box>
    </StyledTableCell>
  );
}

export default function DynamicNestedTable({
  tableData = [],
  tableLoading = false,
  showTypeRow = false,
  columnTypes = {},
  onTypeChange = () => { },
  headers,
  computedColumns = [],
  onDeleteComputedColumn,
  onEditComputedColumn,
}) {
  // Search state
  const [searchValues, setSearchValues] = useState({});

  // Handle search
  const handleSearch = (col, value) => {
    setSearchValues(prev => ({
      ...prev,
      [col]: value
    }));
  };
  // Always treat tableData as an array
  const normalizedTableData = Array.isArray(tableData)
    ? tableData
    : [tableData];

  // Debug logs
  const dynamicColumns = useMemo(
    () =>
      headers && headers.length > 0
        ? headers
        : getAllColumns(normalizedTableData),
    [headers, normalizedTableData]
  );

  //local order for columns, mirrors dynamic columns initially
  const [columnsOrder, setColumnsOrder] = useState(dynamicColumns);
  useEffect(() => {
    // keep in sync if upstream headers change
    if (columnsOrder.join("|") !== dynamicColumns.join("|")) {
      setColumnsOrder(dynamicColumns);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicColumns]);

  // Sorting state
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  // Sorting logic
  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(col);
      setSortDirection("asc");
    }
    setPage(0);
  };

  const filteredAndSortedRows = useMemo(() => {
    // Apply search
    let filtered = normalizedTableData.filter(row => {
      return Object.entries(searchValues).every(([col, searchValue]) => {
        if (!searchValue) return true;
        const value = getValue(row, col);
        return value?.toString().toLowerCase().includes(searchValue.toLowerCase());
      });
    });

    // Apply sorting
    if (!sortColumn) return filtered;

    const sorted = [...filtered].sort((a, b) => {
      const aValue = getValue(a, sortColumn);
      const bValue = getValue(b, sortColumn);
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }
      return sortDirection === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
    return sorted;
  }, [normalizedTableData, sortColumn, sortDirection, searchValues]);

  // Pagination
  const pagedRows = filteredAndSortedRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  //move header
  const moveHeader = (from, to) => {
    setColumnsOrder((prev) => arrayMove(prev, from, to));
  };

  return (
    <StyledCardTableContainer sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
      minHeight: 0,
      position: 'relative'
    }}>
      <StyledTableContainer sx={{
            flex: 1,
            minHeight: 0,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <TableScrollWrapper className="dynamic-table-scroll" sx={{marginBottom:'40px'}}>
            <StyledTable size="small" aria-label="dynamic table" sx={{ minWidth: 'max-content' }}>
              <StyledTableHead>
                <StyledTableRow>
                  {/*use columnsOrder for rendering (reorderable) */}
                  {columnsOrder.map((col, idx) => {
                    const computedCol = computedColumns.find(cc => cc.columnName === col);
                    const isComputed = !!computedCol;
                    return (
                      <SortableHeaderCell
                        key={col}
                        col={col}
                        index={idx}
                        moveHeader={moveHeader}
                        onSort={handleSort}
                        isSorted={sortColumn === col}
                        sortDirection={sortColumn === col ? sortDirection : undefined}
                        onSearch={handleSearch}
                        searchValue={searchValues[col]}
                        isComputedColumn={isComputed}
                        computedColumnFormula={computedCol?.formula || null}
                        onDeleteColumn={onDeleteComputedColumn}
                        onEditComputedColumn={onEditComputedColumn}
                      />
                    );
                  })}
                </StyledTableRow>
                
                {showTypeRow && columnsOrder.length > 0 && (
                  <StyledTableRow>
                    {columnsOrder.map((col, idx) => (
                      <StyledTableCell
                        key={col + "-type"}
                        align={col.includes(".") ? "right" : "left"}
                      >
                        <Dropdown
                          value={columnTypes[col] || "VARCHAR"}
                          onChange={(val) => onTypeChange(col, val)}
                          multiple={false}
                          options={[
                            { value: "VARCHAR", label: "VARCHAR" },
                            { value: "INT", label: "INT" },
                            { value: "FLOAT", label: "FLOAT" },
                            { value: "DATE", label: "DATE" },
                            { value: "BOOLEAN", label: "BOOLEAN" },
                            { value: "TEXT", label: "TEXT" },
                          ]}
                          placeholder="Type"
                          variant="tableHeader"
                        />
                      </StyledTableCell>
                    ))}
                  </StyledTableRow>
                )}
              </StyledTableHead>
              <TableBody>
                {tableLoading
                  ? Array.from({ length: 5 }).map((_, i) => (
                    <StyledTableRow key={i}>
                      <StyledTableCell
                        colSpan={
                          columnsOrder.length // use current order length
                        }
                        sx={{
                          p: 0.7,
                          background: "inherit",
                          border: "none",
                          fontSize: 12,
                        }}
                      >
                        <Skeleton
                          variant="rectangular"
                          height={32}
                          animation="wave"
                          sx={{ borderRadius: 2 }}
                        />
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                  : pagedRows.map((row, rowIdx) => (
                    <StyledTableRow
                      key={row.id || rowIdx}
                      zebra
                      rowIdx={rowIdx}
                    >
                      {columnsOrder.map((col, idx) => (
                        <StyledTableCell
                          key={col}
                          align={col.includes(".") ? "right" : "left"}
                        >
                          {getValue(row, col) === null ||
                            getValue(row, col) === undefined
                            ? ""
                            : getValue(row, col)}
                        </StyledTableCell>
                      ))}
                    </StyledTableRow>
                  ))}
              </TableBody>
            </StyledTable>
            </TableScrollWrapper>
        </StyledTableContainer>
      <Box sx={{ position: 'sticky', bottom: 0, left: 0, right: 0, zIndex: 2 }}>
        <TablePagination
          component="div"
          count={filteredAndSortedRows.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[15, 25, 50, 75]}
        />
      </Box>

    </StyledCardTableContainer>
  );
}
